// src/pages/Onboarding.js
// 5-screen onboarding: Welcome → Existing Website Fork → Modalities → Cost Transparency → Ready
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WelcomeScreen from '../components/onboarding/WelcomeScreen';
import ExistingWebsiteScreen from '../components/onboarding/ExistingWebsiteScreen';
import ModalitiesScreen from '../components/onboarding/ModalitiesScreen';
import CostTransparencyScreen from '../components/onboarding/CostTransparencyScreen';
import ReadyScreen from '../components/onboarding/ReadyScreen';
import firestoreService from '../services/firestore-service';
import { isOldModalityFormat, migrateModalities } from '../utils/migrateModalities';
import { db } from '../config/firebase-config';
import { doc, updateDoc } from 'firebase/firestore';

const Onboarding = () => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(0);
  const [formData, setFormData] = useState({
    displayName: '',
    profilePhoto: null,
    whyThisWork: '',
    hasExistingWebsite: null,
    modalities: { selected: [], custom: [], parentCategories: [] }
  });

  // Helper: background-sync onboarding state to Firestore
  const syncOnboardingToFirestore = (progressData, data, completed = false) => {
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      const onboardingDoc = {
        progress: progressData,
        data: data,
        completed: completed,
        updatedAt: new Date().toISOString()
      };
      firestoreService.saveOnboardingData(userId, onboardingDoc).catch(err => {
        console.warn('⚠️ Firestore onboarding save failed:', err);
      });
    }
  };

  // Resume from saved progress on mount
  useEffect(() => {
    const savedProgress = localStorage.getItem('onboarding_progress');
    const savedData = localStorage.getItem('onboarding_data');

    if (savedProgress && savedData) {
      try {
        setCurrentScreen(parseInt(savedProgress));
        setFormData(JSON.parse(savedData));
      } catch (error) {
        console.error('Error resuming onboarding:', error);
        // Continue with fresh state if parsing fails
      }
    } else {
      // No localStorage data — try Firestore (new device / cleared storage)
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.getOnboardingData(userId).then(firestoreData => {
          if (firestoreData && Object.keys(firestoreData).length > 0) {
            let data = firestoreData.data;

            // One-time migration: convert old flat modality array to new structured format
            if (data && isOldModalityFormat(data.modalities)) {
              console.log('🔄 MIGRATE Onboarding: old modality format detected — migrating');
              data = { ...data, modalities: migrateModalities(data.modalities) };
              // Fire-and-forget: persist migrated data so this only runs once
              firestoreService.saveOnboardingData(userId, {
                ...firestoreData,
                data,
                updatedAt: new Date().toISOString(),
              }).catch(err => console.warn('⚠️ Failed to save migrated onboarding data:', err));
            }

            if (firestoreData.progress !== undefined) {
              localStorage.setItem('onboarding_progress', JSON.stringify(firestoreData.progress));
              setCurrentScreen(firestoreData.progress);
            }
            if (data) {
              localStorage.setItem('onboarding_data', JSON.stringify(data));
              setFormData(data);
            }
          }
        }).catch(err => console.warn('Firestore onboarding read failed:', err));
      }
    }
  }, []);

  // Screen 0 (Welcome) → Screen 1 (Website Fork)
  const handleWelcomeContinue = (data) => {
    const updatedData = { ...formData, ...data };
    setFormData(updatedData);
    localStorage.setItem('onboarding_data', JSON.stringify(updatedData));
    localStorage.setItem('onboarding_progress', '1');
    syncOnboardingToFirestore(1, updatedData);
    setCurrentScreen(1);
  };

  // Screen 1 (Website Fork) → Screen 0 (Back)
  const handleWebsiteForkBack = () => {
    setCurrentScreen(0);
  };

  // Screen 1 (Website Fork) → Screen 2 (Modalities)
  const handleWebsiteForkContinue = (hasExistingWebsite) => {
    const updatedData = { ...formData, hasExistingWebsite };
    setFormData(updatedData);
    localStorage.setItem('onboarding_data', JSON.stringify(updatedData));
    localStorage.setItem('onboarding_progress', '2');
    syncOnboardingToFirestore(2, updatedData);

    // Also persist hasExistingWebsite to Firestore practitioner doc (fire-and-forget)
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      updateDoc(doc(db, 'practitioners', userId), { hasExistingWebsite }).catch(err => {
        console.warn('⚠️ Failed to save hasExistingWebsite:', err);
      });
    }

    setCurrentScreen(2);
  };

  // Screen 2 (Modalities) → Screen 1 (Back)
  const handleModalitiesBack = () => {
    setCurrentScreen(1);
  };

  // Screen 2 (Modalities) → Screen 3 (Cost Transparency)
  const handleModalitiesContinue = (data) => {
    const updatedData = { ...formData, modalities: data };
    setFormData(updatedData);
    localStorage.setItem('onboarding_data', JSON.stringify(updatedData));
    localStorage.setItem('onboarding_progress', '3');
    syncOnboardingToFirestore(3, updatedData);
    setCurrentScreen(3);
  };

  // Screen 2 (Modalities) Skip → Screen 3 (Cost Transparency)
  const handleModalitiesSkip = () => {
    const updatedData = { ...formData, modalities: { selected: [], custom: [], parentCategories: [] } };
    setFormData(updatedData);
    localStorage.setItem('onboarding_data', JSON.stringify(updatedData));
    localStorage.setItem('onboarding_progress', '3');
    syncOnboardingToFirestore(3, updatedData);
    setCurrentScreen(3);
  };

  // Screen 3 (Cost Transparency) → Screen 4 (Ready)
  const handleCostContinue = () => {
    const updatedData = { ...formData, costTransparencySeen: true };
    setFormData(updatedData);
    localStorage.setItem('onboarding_data', JSON.stringify(updatedData));
    localStorage.setItem('onboarding_progress', '4');
    syncOnboardingToFirestore(4, updatedData);
    setCurrentScreen(4);
  };

  // Screen 4 (Ready) → Dashboard (Complete)
  const handleComplete = (tourRequested) => {
    // Save to user profile (currently localStorage, future: Firestore)
    const userProfile = {
      displayName: formData.displayName,
      profilePhoto: formData.profilePhoto,
      whyThisWork: formData.whyThisWork,
      modalities: formData.modalities,
      hasExistingWebsite: formData.hasExistingWebsite,
      onboardingCompleted: true,
      onboardingCompletedAt: new Date().toISOString()
    };

    // Save profile data
    localStorage.setItem('practitioner_profile', JSON.stringify(userProfile));

    // Set completion flags
    localStorage.setItem('onboarding_completed', 'true');
    if (tourRequested) {
      localStorage.setItem('tour_requested', 'true');
    }

    // Sync profile + onboarding completion to Firestore
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      firestoreService.saveProfile(userId, userProfile).catch(err => {
        console.warn('⚠️ Firestore profile save failed:', err);
      });
      syncOnboardingToFirestore(null, formData, true);
    }

    // Clean up onboarding progress keys
    localStorage.removeItem('onboarding_progress');
    localStorage.removeItem('onboarding_data');

    // Navigate: existing-website users → Connect Your Website; others → dashboard
    if (formData.hasExistingWebsite) {
      navigate('/dashboard/settings/connect-website?from=onboarding');
    } else {
      navigate('/dashboard/practitioner');
    }
  };

  // Simple screen rendering - only show current screen
  const renderScreen = () => {
    switch (currentScreen) {
      case 0:
        return (
          <WelcomeScreen
            initialData={{
              displayName: formData.displayName,
              profilePhoto: formData.profilePhoto,
              whyThisWork: formData.whyThisWork
            }}
            onContinue={handleWelcomeContinue}
          />
        );
      case 1:
        return (
          <ExistingWebsiteScreen
            initialValue={formData.hasExistingWebsite}
            onBack={handleWebsiteForkBack}
            onContinue={handleWebsiteForkContinue}
          />
        );
      case 2:
        return (
          <ModalitiesScreen
            initialData={{ modalities: formData.modalities }}
            onBack={handleModalitiesBack}
            onContinue={handleModalitiesContinue}
            onSkip={handleModalitiesSkip}
          />
        );
      case 3:
        return (
          <CostTransparencyScreen
            onContinue={handleCostContinue}
          />
        );
      case 4:
        return (
          <ReadyScreen
            onTakeTour={() => handleComplete(true)}
            onExplore={() => handleComplete(false)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
        backgroundSize: '400% 400%',
        animation: 'auroraShift 16s ease infinite',
      }}
    >
      {/* White card container */}
      <div className="w-full max-w-2xl bg-white rounded-2xl p-8" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-8">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className={`w-2 h-2 rounded-full transition-colors ${currentScreen === i ? 'bg-purple-600' : 'bg-gray-300'}`} />
          ))}
        </div>

        {/* Current screen */}
        <div className="transition-opacity duration-300">
          {renderScreen()}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
